import { FC, useState } from 'react'
import { Box, IconButton, Stack, Typography } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'

import {
  DialogShell,
  NotionButton,
  NotionSelect,
  NotionTextField,
} from '@/apps/latest/components/portal/notion'
import { PORTAL_TEXT } from '@/apps/latest/shared/styles/portalTypography'
import {
  SOAP_TYPE_ORDER,
  type SoapMasterType,
  type SoapSet,
  type SoapSetInput,
  type SoapSetLine,
} from '@/features/soap-masters'

// 定型文の作成・編集。**作成口はここ 1 つ**で、薬歴側からは作らない
// (1 人が薬歴を書いていて押した操作が店舗全員の資産を変える形にしない)。
//
// 詳細ページは作らない (ADR-0017 が収斂させた形と同じ)。

// 区分の無い行は「移行由来の 1 行」だけが取りうる状態で、新規作成では選べない。
// 選択肢に出すのも、既にその状態の定型文を開いたときだけ (= 増やせない)。
const UNTYPED = '__untyped__'

// back の列幅・検証と同じ値 (anycompass.models.SoapSet / serializers.SoapSetLineSerializer)。
// 分類 / 見出し (= condition1 / condition2) / 作成者 は varchar(100)、指導文は serializer が 2000 で切る。
const FIELD_MAX = 100
const CONTENT_MAX = 2000

type TypeValue = SoapMasterType | typeof UNTYPED

const emptyLine = (): SoapSetLine => ({ soap_type: 'S', content: '' })

// **開くたびに mount する** (呼び出し側が key で作り分ける)。state を props から初期化できるので
// 「開いた瞬間に effect で流し込む」が要らず、前回の編集途中が残る事故も起きない。
interface Props {
  onClose: () => void
  // null = 新規作成
  target: SoapSet | null
  onSubmit: (input: SoapSetInput) => Promise<void>
  isSubmitting: boolean
}

export const SoapSetFormDialog: FC<Props> = ({
  onClose,
  target,
  onSubmit,
  isSubmitting,
}) => {
  const [condition1, setCondition1] = useState(target?.condition1 ?? '')
  const [condition2, setCondition2] = useState(target?.condition2 ?? '')
  const [authorName, setAuthorName] = useState(target?.author_name ?? '')
  const [lines, setLines] = useState<SoapSetLine[]>(
    target ? target.lines.map((l) => ({ ...l })) : [emptyLine()]
  )
  const [error, setError] = useState<string | null>(null)

  // 「もともと区分が無かった定型文か」。開いた時点で確定し、編集中は変わらない
  // (区分を付けたあと未設定に戻せてしまうと単調減少が崩れる)。
  const allowUntyped = Boolean(
    target && target.lines.some((l) => l.soap_type === null)
  )

  const hasUntyped = lines.some((l) => l.soap_type === null)

  const typeOptions = [
    ...SOAP_TYPE_ORDER.map((t) => ({ value: t as TypeValue, label: t })),
    ...(allowUntyped ? [{ value: UNTYPED as TypeValue, label: '未設定' }] : []),
  ]

  const updateLine = (index: number, patch: Partial<SoapSetLine>) =>
    setLines((prev) =>
      prev.map((line, i) => (i === index ? { ...line, ...patch } : line))
    )

  const submit = async () => {
    const cleaned = lines
      // 空行は落とすが、**先頭の全角空白は落とさない** (受領マスタの継続行が持つ字下げ)。
      .filter((line) => line.content.trim() !== '')
    if (cleaned.length === 0) {
      setError('指導文を 1 行以上入れてください')
      return
    }
    if (cleaned.some((l) => l.soap_type === null) && cleaned.length > 1) {
      setError('区分が「未設定」の行を含められるのは 1 行のときだけです')
      return
    }
    setError(null)
    await onSubmit({
      condition1: condition1.trim(),
      condition2: condition2.trim(),
      author_name: authorName.trim(),
      lines: cleaned,
    })
  }

  return (
    <DialogShell
      open
      onClose={onClose}
      title={target ? '定型文を編集' : '定型文を追加'}
      disabled={isSubmitting}
      actions={
        <>
          <NotionButton
            variant="secondary"
            onClick={onClose}
            disabled={isSubmitting}
          >
            キャンセル
          </NotionButton>
          <NotionButton
            variant="primary"
            onClick={() => void submit()}
            disabled={isSubmitting}
          >
            保存
          </NotionButton>
        </>
      }
    >
      <Stack gap={2}>
        {/* 「いつ使う定型文か」の見出しを 2 段で持つ。画面では列名 (condition1 / condition2
            = 移行元 CSV の「条件1 / 条件2」) ではなく役割で呼ぶ —— 列名は何を入れる欄かを
            指していない。
            **どちらの段も空でよい。** この器は取込済みの行の編集にも使い、移行元によって
            埋まる段が違う (SOAPセット由来は分類だけ / 文章マスタ由来は見出しだけ)。
            挿入ダイアログの検索もここに当たる。
            maxLength は back の列幅と同じ (varchar 100)。付けないと 101 文字目から
            保存だけが失敗し、なぜ失敗したのかが画面から判らない。 */}
        <NotionTextField
          label="分類"
          value={condition1}
          onChange={(e) => setCondition1(e.target.value)}
          placeholder="小児"
          inputProps={{ maxLength: FIELD_MAX }}
        />
        <NotionTextField
          label="見出し"
          value={condition2}
          onChange={(e) => setCondition2(e.target.value)}
          placeholder="風邪"
          inputProps={{ maxLength: FIELD_MAX }}
        />
        <NotionTextField
          label="作成者"
          value={authorName}
          onChange={(e) => setAuthorName(e.target.value)}
          placeholder="(任意)"
          inputProps={{ maxLength: FIELD_MAX }}
        />

        <Box>
          <Typography sx={{ ...PORTAL_TEXT.caption, mb: 0.5 }}>
            指導文
          </Typography>
          <Stack gap={1}>
            {lines.map((line, i) => (
              <Stack key={i} direction="row" gap={1} alignItems="flex-start">
                <NotionSelect<TypeValue>
                  value={line.soap_type ?? UNTYPED}
                  onChange={(value) =>
                    updateLine(i, {
                      soap_type:
                        value === UNTYPED ? null : (value as SoapMasterType),
                    })
                  }
                  options={typeOptions}
                  minWidth={96}
                  disabled={isSubmitting}
                />
                <NotionTextField
                  value={line.content}
                  onChange={(e) => updateLine(i, { content: e.target.value })}
                  multiline
                  disabled={isSubmitting}
                  inputProps={{ maxLength: CONTENT_MAX }}
                  sx={{ flex: 1 }}
                />
                {lines.length > 1 && (
                  <IconButton
                    size="small"
                    aria-label={`${i + 1} 行目を削除`}
                    disabled={isSubmitting}
                    onClick={() =>
                      setLines((prev) => prev.filter((_, j) => j !== i))
                    }
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                )}
              </Stack>
            ))}
          </Stack>
          {/* 区分が未設定の行があるうちは増やせない (混在した束を作らない)。 */}
          {!hasUntyped && (
            <NotionButton
              variant="ghost"
              onClick={() => setLines((prev) => [...prev, emptyLine()])}
              disabled={isSubmitting}
              sx={{ mt: 1 }}
            >
              <AddIcon fontSize="small" />
              行を追加
            </NotionButton>
          )}
        </Box>

        {error && <Typography sx={PORTAL_TEXT.errorInline}>{error}</Typography>}
      </Stack>
    </DialogShell>
  )
}
